/**
 * Safe decimal formula engine for MBR calculations (PROJECT_SPEC.md §6.1 "[M] Calculation engine —
 * deterministic evaluation order, explicit rounding policy per calculation, decimal arithmetic";
 * CALC-001…006). NO eval()/Function: a shunting-yard parser over a restricted grammar, so an
 * author-defined expression can never execute code or reach object properties (GAMP 5 data integrity,
 * 21 CFR 211.68(b) "formulas … checked for accuracy"). Every intermediate is a decimal.js value at
 * 40 significant digits (CALC-003/005); rounding happens ONCE at the end per the explicit policy
 * (CALC-004) unless the author calls round(x, places) at a specified point in the chain.
 *
 * Ported from Corpex LIMS `apps/api/src/eln/eln-formula.ts` (shunting-yard evaluator for ELN
 * calculation blocks), reduced to the EBR grammar and converted from binary floats to decimal.js.
 */
import { CalcError, D, type Dec, dec, divide, type RoundingMode, round, toPlain } from "./decimal.js";

export type Tok =
  | { t: "num" | "ident"; v: string }
  | { t: "op"; v: "+" | "-" | "*" | "/" | "^" | "neg" }
  | { t: "func"; v: FuncName; argc: number }
  | { t: "lp" | "rp" | "comma"; v: string };

export type FuncName = "min" | "max" | "abs" | "round" | "sqrt";
const FUNCS: ReadonlySet<string> = new Set<FuncName>(["min", "max", "abs", "round", "sqrt"]);
const PREC: Record<string, number> = { "+": 1, "-": 1, "*": 2, "/": 2, "^": 3, neg: 4 };
const RIGHT_ASSOC = new Set(["^", "neg"]);
const IDENT = /^[A-Z][A-Z0-9_]*$/; // UPPER_SNAKE parameter/formula codes (content.ts Code)
const MAX_ROUND_PLACES = 40; // the engine carries 40 significant digits; rounding beyond that is meaningless
const MAX_EXPONENT = 1000; // bounds pow() work; 10^1000 already exceeds any NUMERIC(38) store
const MAX_MAGNITUDE = new D(10).pow(38); // matches decimal.ts MAX_DIGITS / LIM-012

export class FormulaError extends Error {}
const fail = (msg: string): never => {
  throw new FormulaError(msg);
};

const isDigit = (c: string): boolean => c >= "0" && c <= "9"; // ASCII only — no Unicode digits
const isIdentChar = (c: string): boolean => /^[A-Za-z0-9_]$/.test(c);

/** Lexes an expression; throws FormulaError on any character outside the grammar. */
export function tokenize(src: string): Tok[] {
  const toks: Tok[] = [];
  let i = 0;
  while (i < src.length) {
    const c = src[i] as string;
    if (c === " " || c === "\t") {
      i++;
    } else if (isDigit(c)) {
      let j = i + 1;
      while (j < src.length && (isDigit(src[j] as string) || src[j] === ".")) j++;
      const lit = src.slice(i, j);
      if (!/^[0-9]+(\.[0-9]+)?$/.test(lit)) fail(`malformed number "${lit}"`);
      toks.push({ t: "num", v: lit });
      i = j;
    } else if (/^[A-Za-z_]$/.test(c)) {
      let j = i + 1;
      while (j < src.length && isIdentChar(src[j] as string)) j++;
      const name = src.slice(i, j);
      let k = j;
      while (k < src.length && src[k] === " ") k++;
      if (src[k] === "(" && FUNCS.has(name)) toks.push({ t: "func", v: name as FuncName, argc: 1 });
      else if (IDENT.test(name)) toks.push({ t: "ident", v: name });
      else fail(`illegal identifier "${name}" (codes are UPPER_SNAKE; functions: min max abs round sqrt)`);
      i = j;
    } else if (c === "+" || c === "-" || c === "*" || c === "/" || c === "^") {
      const prev = toks[toks.length - 1];
      const unary = c === "-" && (!prev || prev.t === "op" || prev.t === "lp" || prev.t === "comma");
      toks.push({ t: "op", v: unary ? "neg" : c });
      i++;
    } else if (c === "(" || c === ")" || c === ",") {
      toks.push({ t: c === "(" ? "lp" : c === ")" ? "rp" : "comma", v: c });
      i++;
    } else fail(`illegal character "${c}" at position ${i}`);
  }
  if (toks.length === 0) fail("empty expression");
  return toks;
}

/** Parameter/formula codes an expression reads (for the analyzer and forward-reference checks). */
export function referencedIdentifiers(expression: string): string[] {
  return [...new Set(tokenize(expression).flatMap((t) => (t.t === "ident" ? [t.v] : [])))];
}

const popOp = (ops: Tok[], out: Tok[]): void => {
  const o = ops.pop();
  if (o) out.push(o);
};

/** Shunting-yard: infix tokens → RPN. Deterministic; left-assoc for + - * /, right-assoc for ^ and unary minus. */
export function toRpn(toks: readonly Tok[]): Tok[] {
  const out: Tok[] = [];
  const ops: Tok[] = [];
  const argc: number[] = [];
  for (const tk of toks) {
    if (tk.t === "num" || tk.t === "ident") out.push(tk);
    else if (tk.t === "func") {
      ops.push(tk);
      argc.push(1);
    } else if (tk.t === "comma") {
      while (ops.length && ops[ops.length - 1]?.t !== "lp") popOp(ops, out);
      if (!argc.length) fail("comma outside a function call");
      argc[argc.length - 1] = (argc[argc.length - 1] ?? 1) + 1;
    } else if (tk.t === "op") {
      let top = ops[ops.length - 1];
      while (top && top.t === "op") {
        const higher = (PREC[top.v] ?? 0) > (PREC[tk.v] ?? 0);
        const equalLeft = PREC[top.v] === PREC[tk.v] && !RIGHT_ASSOC.has(tk.v);
        if (!higher && !equalLeft) break;
        popOp(ops, out);
        top = ops[ops.length - 1];
      }
      ops.push(tk);
    } else if (tk.t === "lp") ops.push(tk);
    else closeParen(ops, out, argc);
  }
  while (ops.length) {
    const o = ops.pop() as Tok;
    if (o.t === "lp") fail("unbalanced parentheses");
    out.push(o);
  }
  return out;
}

function closeParen(ops: Tok[], out: Tok[], argc: number[]): void {
  while (ops.length && ops[ops.length - 1]?.t !== "lp") popOp(ops, out);
  if (!ops.length) fail("unbalanced parentheses");
  ops.pop();
  const fn = ops[ops.length - 1];
  // bind the arg count at the closing paren so nested/multiple calls pair correctly
  if (fn && fn.t === "func") out.push({ ...(ops.pop() as typeof fn), argc: argc.pop() ?? 1 });
}

export interface EvalOptions {
  precision: number;
  rounding: RoundingMode;
}
export type FormulaResult = { ok: true; value: string } | { ok: false; error: string };

function applyFunc(name: FuncName, args: Dec[], mode: RoundingMode): Dec {
  const a = args[0];
  if (!a) return fail(`${name}() needs at least one argument`);
  switch (name) {
    case "min":
      return args.reduce((m, x) => (x.lt(m) ? x : m));
    case "max":
      return args.reduce((m, x) => (x.gt(m) ? x : m));
    case "abs":
      return a.abs();
    case "sqrt":
      if (a.isNegative()) fail("sqrt() of a negative value");
      return a.sqrt();
    case "round": {
      // an author-specified rounding point in the chain (CALC-003); uses the calculation's mode (CALC-004)
      const places = args[1];
      if (args.length !== 2 || !places || !places.isInteger() || places.isNegative())
        fail("round(x, places) needs a non-negative integer places");
      // bounded input at the trust boundary: an author-supplied place count never reaches the decimal engine
      // unchecked, however cheap that engine makes an oversized argument today
      if ((places as Dec).gt(MAX_ROUND_PLACES))
        fail(`round(x, places) allows at most ${MAX_ROUND_PLACES} places`);
      return round(a, { places: (places as Dec).toNumber(), mode });
    }
  }
}

function applyOp(op: string, a: Dec, b: Dec): Dec {
  switch (op) {
    case "+":
      return a.plus(b);
    case "-":
      return a.minus(b);
    case "*":
      return a.times(b);
    case "/":
      return divide(a, b); // CALC-006: throws CalcError DIVISION_BY_ZERO
    default:
      if (b.abs().gt(MAX_EXPONENT)) fail(`exponent ${b.toString()} exceeds ${MAX_EXPONENT}`);
      if (a.isNegative() && !b.isInteger()) fail("negative base with a fractional exponent");
      return a.pow(b);
  }
}

function lookup(inputs: Record<string, string>, name: string): Dec {
  // own-property check only: "CONSTRUCTOR", "__PROTO__" etc. can never resolve through the prototype
  if (!Object.hasOwn(inputs, name)) fail(`unknown identifier ${name}`);
  return dec(inputs[name] as string);
}

function evalRpn(rpn: readonly Tok[], inputs: Record<string, string>, mode: RoundingMode): Dec {
  const st: Dec[] = [];
  for (const tk of rpn) {
    if (tk.t === "num") st.push(new D(tk.v));
    else if (tk.t === "ident") st.push(lookup(inputs, tk.v));
    else if (tk.t === "op" && tk.v === "neg") st.push((st.pop() ?? fail("missing operand")).negated());
    else if (tk.t === "op") {
      const b = st.pop();
      const a = st.pop();
      if (!a || !b) fail(`missing operand for ${tk.v}`);
      st.push(applyOp(tk.v, a as Dec, b as Dec));
    } else if (tk.t === "func") {
      if (st.length < tk.argc) fail(`${tk.v}() missing arguments`);
      st.push(applyFunc(tk.v, st.splice(st.length - tk.argc, tk.argc), mode));
    }
  }
  if (st.length !== 1) fail("malformed expression");
  return st[0] as Dec;
}

/**
 * Evaluates `expression` over string-decimal `inputs`. Left-to-right deterministic order; intermediates at
 * precision 40; the result is rounded exactly once to `opts.precision` places with `opts.rounding`
 * (CALC-003/004) and returned as a plain fixed-point string (CALC-005). Never throws: refusals are
 * `{ ok: false, error }` so a record never carries NaN/Infinity (CALC-006).
 */
export function evaluateFormula(
  expression: string,
  inputs: Record<string, string>,
  opts: EvalOptions,
): FormulaResult {
  try {
    if (!Number.isInteger(opts.precision) || opts.precision < 0) fail(`bad precision ${opts.precision}`);
    const value = evalRpn(toRpn(tokenize(expression)), inputs, opts.rounding);
    if (!value.isFinite()) fail("result is not finite");
    if (value.abs().gte(MAX_MAGNITUDE)) fail("result exceeds 38 digits (LIM-012)");
    return { ok: true, value: toPlain(value, { places: opts.precision, mode: opts.rounding }) };
  } catch (e) {
    if (e instanceof FormulaError || e instanceof CalcError) return { ok: false, error: e.message };
    throw e; // a defect in this file, not a data problem — surface it
  }
}
